import { api } from 'api/axios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { PatientInfo } from './usePatient';

/** Single patient info record from the patient-infos API */
export interface PatientInfoRecord {
  id: string;
  userId: string;
  info: PatientInfo;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

/** List response */
export interface PatientInfoListResponse {
  items: PatientInfoRecord[];
  total: number;
}

/** Payload for create/update (uses postalCode, not zipPrefix/zipSuffix) */
export interface PatientInfoPayload {
  info: PatientInfo;
  isDefault?: boolean;
}

const QUERY_KEY = ['patient-infos'];

export async function listPatientInfos(): Promise<PatientInfoListResponse> {
  const response = await api.get<PatientInfoListResponse>(
    '/medicalcare/patient-infos',
  );
  return response.data;
}

export async function getPatientInfo(
  patientInfoId: string,
): Promise<PatientInfoRecord> {
  const response = await api.get<PatientInfoRecord>(
    `/medicalcare/patient-infos/${patientInfoId}`,
  );
  return response.data;
}

export async function createPatientInfo(
  data: PatientInfoPayload,
): Promise<PatientInfoRecord> {
  const response = await api.post<PatientInfoRecord>(
    '/medicalcare/patient-infos',
    data,
  );
  return response.data;
}

export async function updatePatientInfo(
  patientInfoId: string,
  data: Partial<PatientInfoPayload>,
): Promise<PatientInfoRecord> {
  const response = await api.put<PatientInfoRecord>(
    `/medicalcare/patient-infos/${patientInfoId}`,
    data,
  );
  return response.data;
}

export async function deletePatientInfo(patientInfoId: string): Promise<void> {
  await api.delete(`/medicalcare/patient-infos/${patientInfoId}`);
}

export async function setDefaultPatientInfo(
  patientInfoId: string,
): Promise<PatientInfoRecord> {
  const response = await api.put<PatientInfoRecord>(
    `/medicalcare/patient-infos/${patientInfoId}/set-default`,
    {},
    { headers: { Accept: '*/*' } },
  );
  return response.data;
}

export function usePatientInfos(token?: string) {
  const { data, ...rest } = useQuery<unknown, unknown, PatientInfoListResponse>(
    QUERY_KEY,
    () => listPatientInfos(),
    {
      enabled: !!token,
    },
  );
  return {
    patientInfos: data?.items ?? [],
    total: data?.total ?? 0,
    ...rest,
  };
}

export function usePatientInfoRecord(patientInfoId: string | undefined) {
  const { data, ...rest } = useQuery<unknown, unknown, PatientInfoRecord>(
    [...QUERY_KEY, patientInfoId],
    () => getPatientInfo(patientInfoId!),
    { enabled: !!patientInfoId },
  );
  return { patientInfo: data, ...rest };
}

export function useCreatePatientInfoRecord() {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<
    PatientInfoRecord,
    unknown,
    PatientInfoPayload
  >((data) => createPatientInfo(data), {
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY);
    },
  });
  return { createPatientInfo: mutateAsync, ...rest };
}

export function useUpdatePatientInfoRecord() {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<
    PatientInfoRecord,
    unknown,
    { patientInfoId: string; data: PatientInfoPayload }
  >(({ patientInfoId, data }) => updatePatientInfo(patientInfoId, data), {
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY);
    },
  });
  return { updatePatientInfo: mutateAsync, ...rest };
}

export function useDeletePatientInfoRecord() {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<void, unknown, string>(
    (patientInfoId) => deletePatientInfo(patientInfoId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QUERY_KEY);
      },
    },
  );
  return { deletePatientInfo: mutateAsync, ...rest };
}

// export function useSetDefaultPatientInfo() {
//   const queryClient = useQueryClient();
//   const { mutateAsync, ...rest } = useMutation<
//     PatientInfoRecord,
//     unknown,
//     string
//   >((patientInfoId) => updatePatientInfo(patientInfoId, { isDefault: true }), {
//     onSuccess: () => {
//       queryClient.invalidateQueries(QUERY_KEY);
//     },
//   });
//   return { setDefaultPatientInfo: mutateAsync, ...rest };
// }

export function useSetDefaultPatientInfo() {
  const queryClient = useQueryClient();
  const { mutateAsync, ...rest } = useMutation<
    PatientInfoRecord,
    unknown,
    string
  >((patientInfoId) => setDefaultPatientInfo(patientInfoId), {
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY);
    },
  });
  return { setDefaultPatientInfo: mutateAsync, ...rest };
}
